import { IonFab, IonFabButton, useIonModal } from '@ionic/react';
import { EmptyFeedFilter, FeedFilterParamType, FullPost } from 'model';
import React, { useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useGetFeedQuery } from '../api/FeedApi';
import { CreatePost } from '../components/CreatePost';
import { FeedLogin } from '../components/FeedLogin';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { LoginStatusAvatar } from '../components/LoginStatusAvatar';
import { PostListItem } from '../components/PostListItem';
import { IconClose, IconFilter, IconPen, IconSearch } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { FeedFiltersContext, useFeedFilters } from '../hooks/useFeedFilters';
import { FeedFilters } from './FeedFilters';

// Styles
import { useDispatch } from 'react-redux';
import { QuirkLoader } from '../components/QuirkLoader';
import { useFeedOrder } from '../hooks/useFeedOrder';
import { useLoggedIn } from '../hooks/useLoggedIn';
import { useLoginState } from '../hooks/useLoginState';
import { usePostCreateActionSheet } from '../hooks/usePostCreateActionSheet';
import { setFeedOrder } from '../state/feedOrderSlice';
import { useGetPromotedQuery } from '../api/PostApi.js';
import { useGetPublicInformationQuery } from '../api/SiteSettingsAPI.js';
import { usePromotedFeed } from '../hooks/usePromotedFeed.js';

type FeedInsert = {
	type: 'create' | 'promotion' | 'custom';
	label?: string;
};

const FeedView: React.FC<{ item: FullPost | FeedInsert }> = ({ item }) => {
	const loggedIn = useLoggedIn();
	if (item.type === 'post') {
		// Hide ask the community and polls from anonymous users
		if (loggedIn.realUser || item.postType === 'basic' || item.postType === 'event') {
			return <PostListItem key={item._id} post={item} />;
		} else {
			return <div style={{ opacity: 0, height: 1 }}></div>;
		}
	}
	if (item.type === 'create') {
		return (
			<>{loggedIn.realUser ? <CreatePost label={item.label} /> : <FeedLogin label={'Login to create posts'} />}</>
		);
	}

	return <div>Not implemented</div>;
};

export const Feed = React.forwardRef((props, ref) => {
	const { data: siteSettings } = useGetPublicInformationQuery();
	const presentPostCreateOptions = usePostCreateActionSheet();
	const [feedFilterValues, setFeedFilterValues] = useState<FeedFilterParamType>(EmptyFeedFilter);
	const loginState = useLoginState();
	const loginValid = loginState.loggedIn === 'valid';

	const { data: feed1 = [], isLoading } = useGetFeedQuery(undefined, { skip: !loginValid });
	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(setFeedOrder(feed1));
	}, [dispatch, feed1]);

	const { data: promotedPosts, isLoading: isLoadingPromoted } = useGetPromotedQuery(undefined, { skip: !loginValid });

	// Order the feed like it was last ordered
	const feed = useFeedOrder(feed1);
	const { fullFeed } = useFeedFilters(feedFilterValues, feed);
	const finalFeed = usePromotedFeed(fullFeed, promotedPosts, siteSettings?.feedPromotionDensity);
	const feedData: (FullPost | FeedInsert)[] = [
		{ type: 'create', label: 'Create new...' },
		...(finalFeed ?? []),
		{ type: 'create', label: `Done? Add something new...` }
	];

	const setValues = (val: FeedFilterParamType) => {
		const updatedValues: FeedFilterParamType = feedFilterValues;
		setFeedFilterValues({ ...updatedValues, ...val });
	};

	// Report filter page
	const [presentFeedFilter, dismissFeedFilter] = useIonModal(FeedFilters, {
		feedFilterValues,
		setValues,
		onDismiss: () => dismissFeedFilter()
	});

	const checkIfFilters = () => {
		return feedFilterValues.filterBy !== 'all' || feedFilterValues.topic !== '';
	};

	return (
		<QuirkIonPage name="Feed" roles={['user', 'anonymous']}>
			<Header
				title="logo"
				left={
					<div>
						<LoginStatusAvatar />
					</div>
				}
				right={
					<div>
						{checkIfFilters() ? (
							<IconButton
								label="Clear Filters"
								icon={<IconClose />}
								onClick={() => setFeedFilterValues(EmptyFeedFilter)}
							/>
						) : (
							<IconButton icon={<IconFilter />} onClick={presentFeedFilter} testId="feedFilterButton" />
						)}
					</div>
				}
			/>
			<QuirkIonContent flush scroll={false} testId="feedList">
				<FeedFiltersContext.Provider
					value={{
						filters: feedFilterValues,
						setFilters: setValues
					}}
				>
					<div className="ionFabContainer">
						<IonFab vertical="bottom" horizontal="end">
							<IonFabButton
								className="IonFabButton"
								color="secondary"
								onClick={presentPostCreateOptions}
								data-testid="floatingCreatePostButton"
							>
								<IconPen size="large" />
							</IonFabButton>
						</IonFab>
					</div>
					<QuirkLoader
						isLoading={isLoading || isLoadingPromoted}
						loadingMessage="Loading feed..."
						data={finalFeed}
						noResults={
							<QuirkIonContent fullscreen={true}>
								<div className="center-align">
									<IconButton icon={<IconSearch />} className="icon-graphic" />
									<div className="space20" />
									<h4>No results found.</h4>
									{checkIfFilters() && <p>Refine or clear your search filters.</p>}
								</div>
							</QuirkIonContent>
						}
					>
						<Virtuoso
							ref={ref as any}
							totalCount={feedData?.length}
							data={feedData}
							overscan={{ main: 2000, reverse: 2000 }}
							itemContent={(index, item) => (
								<div>
									<FeedView item={item} />
								</div>
							)}
						/>
					</QuirkLoader>
				</FeedFiltersContext.Provider>
			</QuirkIonContent>
		</QuirkIonPage>
	);
});
